import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useState } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';

const graphScopes = ['https://graph.microsoft.com/User.ReadBasic.All'];

const graphSWRConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  shouldRetryOnError: false,
};

export type GraphUser = {
  id: string;
  displayName: string;
  givenName: string | null;
  surname: string | null;
  mail: string | null;
  userPrincipalName: string | null;
};

const userSelectFields = [
  'id',
  'displayName',
  'givenName',
  'surname',
  'mail',
  'userPrincipalName',
].join(',');

export const useGraphEnabled = (oid: string | null | undefined) =>
  Boolean(oid) && !import.meta.env.VITE_ACCESS_TOKEN;

const useGraphToken = () => {
  const { accounts, instance } = useMsal();

  const getToken = useCallback(async (): Promise<string> => {
    const result = await instance.acquireTokenSilent({
      account: accounts[0],
      scopes: graphScopes,
    });
    return result.accessToken;
  }, [accounts, instance]);

  return getToken;
};

export function useGraphUser(oid: string | null | undefined) {
  const getToken = useGraphToken();
  const enabled = useGraphEnabled(oid);

  const fetcher = useCallback(async (): Promise<GraphUser> => {
    const accessToken = await getToken();
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(oid!)}?$select=${userSelectFields}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error(`Graph API error: ${response.status}`);
    }

    return (await response.json()) as GraphUser;
  }, [oid, getToken]);

  return useSWR<GraphUser>(enabled ? `graph/users/${oid}` : null, fetcher, graphSWRConfig);
}

function useObjectUrl(blob: Blob | undefined) {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return url;
}

export function useGraphUserPhoto(oid: string | null | undefined) {
  const getToken = useGraphToken();
  const enabled = useGraphEnabled(oid);

  const fetcher = useCallback(async (): Promise<Blob> => {
    const accessToken = await getToken();
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(oid!)}/photo/$value`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) {
      throw new Error(`Graph API photo error: ${response.status}`);
    }

    return await response.blob();
  }, [oid, getToken]);

  const { data: blob, ...rest } = useSWR<Blob>(
    enabled ? `graph/users/${oid}/photo` : null,
    fetcher,
    graphSWRConfig
  );
  const url = useObjectUrl(blob);

  return { data: url, ...rest };
}
